import { useState, useContext } from 'react';

import Text from '@/components/Text';
import AppointmentPicker from '@/pages/care-team/AppointmentPicker';
import ProviderHeadshot from '@/pages/care-team/ProviderHeadshot';
import { providerLicenses, supportEmailPsych } from '@/utils/constants';
import FullWidthHeader from '../FullWidthHeader';
import AdditionalAssistanceFooter from './AdditionalAssistanceFooter';
import OldAppointmentCard from './OldAppointmentCard';
import {
  ActiveProviderShapeV3,
  InactiveProviderShape,
  Location,
  OldAppointmentProps,
  SeriesType,
  TherapyType,
} from '@/utils/types';
import InlineMessageBox from '../InlineMessageBox';
import SupportLink from '@/components/SupportLink';
import { SessionFormatToggle } from '@pathccm/path-components-provider-search';
import { getBookableSlotsForProvider } from '@/utils/appointments';
import { StateContext } from '@/App';
import { useFlagCheck } from '@/utils/use-feature-flags';
import { SessionType } from '@/components/appointments/SessionType';
import { WithIcon } from '@/components/appointments/WithIcon';
import { ProviderMap } from '@/components/appointments/ProviderMap';

type ChooseAppointmentProps = {
  provider: ActiveProviderShapeV3 | InactiveProviderShape;
  hasBookableAppointments: boolean;
  therapyType: TherapyType;
  seriesType: SeriesType;
  appointmentLengthInMinutes: number;
  oldAppointmentProps?: OldAppointmentProps;
  isHold: boolean;
  isReactivation?: boolean;
  onSendMessage?: (message: string) => unknown;
};

const ChooseAppointment = ({
  provider,
  hasBookableAppointments,
  therapyType,
  seriesType,
  appointmentLengthInMinutes,
  oldAppointmentProps,
  isHold,
  isReactivation,
  onSendMessage = () => {},
}: ChooseAppointmentProps) => {
  const { state } = useContext(StateContext);

  // Always default to telemedicine until in person followup booking is launched
  const [selectedSessionFormat, setSelectedSessionFormat] =
    useState<Location>('telemedicine');

  const isPsych = therapyType === 'psychiatric';

  const providerDefaultPractice = provider.practice?.default_in_person_location;
  const providerOffersInPerson = providerDefaultPractice?.is_active || false;
  const { isAllowed } = useFlagCheck();

  const inPersonFollowupBookingEnabled =
    !isReactivation && // No in-person followup booking for re-activations (for now)
    !isPsych && // No in-person followup booking for psychiatry providers (for now)
    providerOffersInPerson && // Provider must offer in-person sessions and have a default address
    isAllowed({
      patientPortalEnableInPersonFollowupBooking: true,
    });

  const getBookableSlots = (sessionFormat: Location) => {
    return getBookableSlotsForProvider(
      state.ui.usedAvailabilitySlots,
      provider,
      seriesType,
      sessionFormat,
    );
  };

  return (
    <div className="space-y-10">
      {/* Header/navigation area */}
      <FullWidthHeader returnPath="/appointments" returnPage="appointments">
        Choose a new appointment
      </FullWidthHeader>

      {/* Body */}
      <div className="flex flex-wrap justify-center lg:flex-nowrap w-full lg:space-x-8">
        {/* Select new appointment */}
        <div className="border-1 border-grey-200 rounded flex-auto px-6 py-8 w-full lg:max-w-[715px]">
          {/* Provider header */}
          <div className="flex relative justify-start items-center gap-x-6">
            <ProviderHeadshot
              profileImage={provider.profile_image_s3_url}
              className="block w-20 h-20 self-start mb-4"
            />
            <div className="w-full">
              <div className="font-semibold mt-0 mb-3 text-xl text-left">
                <Text.H3 className="text-grey-800 mb:2">
                  {provider.first_name} {provider.last_name}
                </Text.H3>
                <Text.Small className="text-grey-800 pb-4">
                  {providerLicenses[provider.licenses[0]]?.name || ''}
                </Text.Small>
                {/* TODO: handle multiple licenses */}
              </div>
            </div>
          </div>

          {hasBookableAppointments ? (
            <>
              {inPersonFollowupBookingEnabled && (
                <div className="pb-4 border-t border-grey-200">
                  <Text.H4.Bold className="mb-4 mt-4">
                    Session type
                  </Text.H4.Bold>
                  <SessionFormatToggle
                    sessionFormat={selectedSessionFormat}
                    onChange={setSelectedSessionFormat}
                  />
                </div>
              )}
              <div className="border-b border-t border-grey-200">
                <AppointmentPicker
                  generateLinkProps={(slotDate) => ({
                    to: `${slotDate}?location=${selectedSessionFormat}${isReactivation ? '&reactivate=true' : ''}`,
                  })}
                  provider={provider}
                  therapyType={therapyType}
                  isHold={isHold}
                  bookableSlots={getBookableSlots(selectedSessionFormat)}
                />
              </div>
              <div className="flex flex-col items-left gap-4 text-grey-800 mt-4">
                <WithIcon icon="Clock">
                  <Text.P>{appointmentLengthInMinutes} minutes</Text.P>
                </WithIcon>
                <SessionType location={selectedSessionFormat} />
              </div>
              {inPersonFollowupBookingEnabled &&
                providerDefaultPractice?.address && (
                  <ProviderMap
                    providerAddress={providerDefaultPractice.address}
                    visible={selectedSessionFormat === 'in_person'}
                  />
                )}
            </>
          ) : (
            <div className="flex flex-col items-start justify-center border-t border-grey-200 pt-4">
              {isPsych ? (
                <>
                  <Text.P>
                    {provider.first_name} has no upcoming availability
                  </Text.P>
                  <Text.P>
                    For assistance contact{' '}
                    <SupportLink
                      email={supportEmailPsych}
                      text={supportEmailPsych}
                    />
                  </Text.P>
                </>
              ) : (
                <>
                  <Text.Small className="py-4">
                    {provider.first_name} has no upcoming availability. Message
                    them to inquire.
                  </Text.Small>
                  <InlineMessageBox onSendMessage={onSendMessage} />
                </>
              )}
            </div>
          )}
        </div>

        {/* Old appointment info */}
        {oldAppointmentProps && (
          <OldAppointmentCard
            startTime={oldAppointmentProps.startTime}
            isFirstAppointment={oldAppointmentProps.isFirstAppointment}
            therapyType={therapyType}
            appointmentType={oldAppointmentProps.appointmentType}
            previousLengthInMinutes={
              oldAppointmentProps.previousLengthInMinutes
            }
            location={oldAppointmentProps.location}
          />
        )}
      </div>

      {/* Footer */}
      {hasBookableAppointments && (
        <div className="flex justify-center">
          <AdditionalAssistanceFooter />
        </div>
      )}
    </div>
  );
};

export default ChooseAppointment;
